/**
 * 模板商城：整页、按分类逛、每张都是缩略图，点「使用」直接进大画布。
 * 照竞品的版式。以前是一个 460px 的弹窗，看不到样子只能看名字。
 */
import { useMemo, useState } from 'react';
import { Button, Input, message } from 'antd';
import { LeftOutlined, SearchOutlined } from '@ant-design/icons';
import type { FieldMeta } from '../api/types';
import { useAppDispatch, useAppState } from '../store/hooks';
import { useContractTemplates } from '../contract/useContractTemplates';
import { LIBRARY, STORE_GROUPS, applyLibraryTemplate, storeGroup, type LibraryTemplate, type StoreGroup } from '../templates/library';
import { TemplateThumb } from '../components/shell/TemplateThumb';
import { launchFullscreen } from '../fullscreen/launch';
import { track } from '../utils/track';

export function TemplateStorePage({ fieldMetas }: { fieldMetas: FieldMeta[] }) {
  const state = useAppState();
  const dispatch = useAppDispatch();
  const tableId = state.context?.tableId ?? '';
  const contractTpl = useContractTemplates(state.context?.tableId ?? null);
  const [group, setGroup] = useState<'全部' | StoreGroup>('全部');
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);

  const list = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return LIBRARY.filter((lib) => (group === '全部' || storeGroup(lib.category) === group) && (!kw || lib.name.toLowerCase().includes(kw)));
  }, [group, q]);

  const use = (lib: LibraryTemplate) => {
    if (!state.context) {
      message.warning('正在连接数据表，请稍候再试');
      return;
    }
    track('tpl_create');
    if (state.mode !== 'contract') dispatch({ type: 'SET_MODE', mode: 'contract' });
    const t = contractTpl.insert(applyLibraryTemplate(lib, tableId || '__no_table__'));
    if (!t) return;
    void launchFullscreen({ template: t, tableId, recordId: state.contractRecordId, fieldMetas }).then((r) => {
      if (r !== 'opened') dispatch({ type: 'NAV', view: 'editor', editingTemplateId: t.id });
    });
  };

  const pill = (label: string, on: boolean, onClick: () => void) => (
    <button
      key={label}
      className="pp-bare-button"
      onClick={onClick}
      style={{
        padding: '5px 14px',
        borderRadius: 999,
        fontSize: 13,
        fontWeight: on ? 700 : 500,
        background: on ? 'var(--accent)' : 'var(--surface-2)',
        color: on ? 'var(--accent-ink)' : 'var(--ink-2)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* 顶栏：返回 / 标题居中 / 搜索 */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--border-soft)', gap: 8 }}>
        <Button type="text" icon={<LeftOutlined />} onClick={() => dispatch({ type: 'NAV', view: 'home', editingTemplateId: null })}>
          返回
        </Button>
        <div style={{ flex: 1, textAlign: 'center', fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>📦 模板商城</div>
        {searching ? (
          <Input
            size="small"
            autoFocus
            allowClear
            placeholder="搜索单据"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onBlur={() => !q && setSearching(false)}
            style={{ width: 160 }}
          />
        ) : (
          <Button type="text" icon={<SearchOutlined />} onClick={() => setSearching(true)} />
        )}
      </div>

      <div className="pp-scroll" style={{ flex: 1, overflowY: 'auto', padding: '10px 14px 24px' }}>
        <div className="pp-scroll" style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginBottom: 6 }}>
          {pill('全部', group === '全部', () => setGroup('全部'))}
          {STORE_GROUPS.map((g) => pill(g, group === g, () => setGroup(g)))}
        </div>

        {list.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>没有匹配的单据</div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
          {list.map((lib) => (
            <div
              key={lib.key}
              style={{ border: '1px solid var(--border-soft)', background: 'var(--surface-1)', borderRadius: 12, overflow: 'hidden' }}
            >
              <TemplateThumb template={applyLibraryTemplate(lib, tableId || '__no_table__')} fieldMetas={fieldMetas} height={180} />
              <div style={{ padding: '10px 14px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {lib.name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{storeGroup(lib.category)} · {lib.category}</div>
                </div>
                <Button type="primary" onClick={() => use(lib)}>
                  使用
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
